import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle, Calendar, MapPin, CreditCard, MessageCircle } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useSession } from '@/state/SessionProvider';

interface Booking {
  id: string;
  listing_id: string;
  start_at: string;
  end_at: string;
  total_amount: number;
  currency: string;
  status: string;
  listings: {
    title: string;
    address: string;
  };
}

export default function Success() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useSession();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetched = useRef(false);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setError('No session ID found');
      setLoading(false);
      return;
    }

    // Prevent double calls (React strict mode or multiple renders)
    if (hasFetched.current) {
      return;
    }
    hasFetched.current = true;

    const fetchBooking = async () => {
      try {
        console.log('✅ Verifying payment for session:', sessionId);
        
        // Call verify-payment to create booking if payment successful
        const { data, error } = await supabase.functions.invoke('verify-payment', {
          body: { session_id: sessionId }
        });

        if (error) throw error;
        
        // Check if response has an error field
        if (data?.error) {
          throw new Error(data.error);
        }

        if (!data?.success || !data?.booking_id) {
          throw new Error('Betaling kunne ikke verificeres');
        }

        console.log('✅ Payment verified, booking ID:', data.booking_id);

        // Fetch the booking details
        const { data: bookingData, error: bookingError } = await supabase
          .from('bookings')
          .select(`
            id,
            listing_id,
            start_at,
            end_at,
            total_amount,
            currency,
            status,
            listings (
              title,
              address
            )
          `)
          .eq('id', data.booking_id)
          .single();

        if (bookingError) throw bookingError;

        if (bookingData) {
          setBooking(bookingData as Booking);
          console.log('✅ Booking details retrieved:', bookingData);

        } else {
          throw new Error('Booking ikke fundet');
        }
      } catch (err) {
        console.error('Error fetching booking:', err);
        setError(err instanceof Error ? err.message : 'Failed to retrieve booking');
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [searchParams]);

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 0,
    }).format(amount / 100); // Convert from øre to kroner
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('da-DK', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const calculateDuration = (startAt: string, endAt: string) => {
    const start = new Date(startAt);
    const end = new Date(endAt);
    const durationMs = end.getTime() - start.getTime();
    const days = Math.ceil(durationMs / (1000 * 60 * 60 * 24));
    return days;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <Card className="max-w-2xl mx-auto">
            <CardHeader className="text-center">
              <Skeleton className="h-8 w-48 mx-auto mb-2" />
              <Skeleton className="h-4 w-64 mx-auto" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-16 w-full" />
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-4 sm:py-8">
          <Card className="max-w-2xl mx-auto">
            <CardHeader className="text-center px-4 sm:px-6">
              <CardTitle className="text-lg sm:text-xl text-destructive">Booking Fejl</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4 px-4 sm:px-6">
              <p className="text-sm sm:text-base text-muted-foreground whitespace-pre-wrap">
                {error || 'Booking kunne ikke findes'}
              </p>
              {error?.includes('allerede booket') && (
                <div className="bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 text-left">
                  <p className="text-sm font-semibold mb-2">Hvad nu?</p>
                  <p className="text-sm text-muted-foreground">
                    Din betaling er gennemført, men datoerne er desværre ikke længere tilgængelige. 
                    Kontakt venligst support på askyum@hotmail.com for at få refunderet beløbet.
                  </p>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={() => navigate('/dashboard')} className="flex-1">
                  Gå til Dashboard
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/')}
                  className="flex-1"
                >
                  Find andre parkeringspladser
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const duration = calculateDuration(booking.start_at, booking.end_at);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-4 sm:py-8">
        <Card className="max-w-2xl mx-auto">
          <CardHeader className="text-center px-4 sm:px-6">
            <div className="flex justify-center mb-4">
              <CheckCircle className="h-12 w-12 sm:h-16 sm:w-16 text-green-500" />
            </div>
            <CardTitle className="text-xl sm:text-2xl text-green-600">
              Booking Bekræftet!
            </CardTitle>
            <p className="text-sm sm:text-base text-muted-foreground">
              Din parkering er nu reserveret
            </p>
          </CardHeader>
          <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6">
            {/* Booking Details */}
            <div className="bg-muted/50 rounded-lg p-3 sm:p-4 space-y-3">
              <div className="flex items-start gap-2 sm:gap-3">
                <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground mt-1 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-sm sm:text-base truncate">{booking.listings.title}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2">
                    {booking.listings.address}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-2 sm:gap-3">
                <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground mt-1 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-xs sm:text-sm break-words">
                    {formatDate(booking.start_at)} - {formatDate(booking.end_at)}
                  </p>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {duration} {duration === 1 ? 'dag' : 'dage'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 sm:gap-3">
                <CreditCard className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground flex-shrink-0" />
                <div>
                  <p className="font-semibold text-sm sm:text-base">
                    {formatAmount(booking.total_amount, booking.currency)}
                  </p>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Betaling gennemført
                  </p>
                </div>
              </div>
            </div>

            {/* Chat Section */}
            <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-3 sm:p-4">
              <h4 className="font-semibold mb-2 flex items-center gap-2 text-sm sm:text-base">
                <MessageCircle className="h-4 w-4" />
                Chat med værten
              </h4>
              <p className="text-xs sm:text-sm text-muted-foreground mb-3">
                Værten har sendt dig en velkomstbesked. Gå til beskeder i dit dashboard for at koordinere 
                parkeringsdetaljer, p-tilladelser og adgang.
              </p>
              <Button 
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="w-full text-sm sm:text-base"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Gå til beskeder
              </Button>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                onClick={() => navigate('/dashboard')}
                className="flex-1"
              >
                Gå til Dashboard
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/')}
                className="flex-1"
              >
                Find flere parkeringspladser
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}